import dedent from 'ts-dedent'
import {
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class RemoveDuplicatedRows extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.removeduplicatedrows',
        group: 'Data Preparation',
        title: 'Remove Duplicated',
        description: `
      This component takes an input table and generates a new one in which duplicates rows from the input table have been removed.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    let query = ''
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    switch (connection.type) {
      case Provider.BigQuery: {
        const select = `SELECT %s1
                        FROM (SELECT DISTINCT %s2 FROM ${connection.quoteName(preparedParams.source as string)})`
        const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        const tokens = (preparedParams.source as string)?.split('.')
        const datasetname = storedProc ? connection.tempLocation : `${tokens[0]}.${tokens[1]}`
        const tablename = storedProc ? `${tempTableName(componentName, preparedParams.source)}` : tokens[2]
        const storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
        query = dedent`DECLARE alias_encoded STRING;
              DECLARE alias_decoded STRING;
              ${
                storedProc
                  ? dedent`
                      CREATE TABLE ${storedProcTable} AS
                      SELECT * FROM ${connection.quoteName(preparedParams.source as string)}
                      WHERE 1=0;`
                  : ''
              }
              EXECUTE IMMEDIATE
              '''
                with __alias AS(
                  SELECT REPLACE(
                    CASE data_type WHEN 'GEOGRAPHY' THEN 'ST_ASBINARY(%s) AS %s' ELSE '%s AS %s' END
                  , '%s', column_name) col_alias_encoded,
                  REPLACE(
                    CASE data_type WHEN 'GEOGRAPHY' THEN 'ST_GEOGFROMWKB(%s) AS %s' ELSE '%s AS %s' END
                  , '%s', column_name) col_alias_decoded
                  FROM \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                WHERE table_name = '${tablename}'
                )
                SELECT STRING_AGG(col_alias_encoded, ', '), STRING_AGG(col_alias_decoded, ', ')
                FROM __alias
              '''
              INTO alias_encoded, alias_decoded;
              ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
              ${dedent(
                connection.executeDynamicSQL({
                  sql: createTable,
                  addQuotes: true,
                  replacements: [
                    ["'%s1'", 'alias_decoded'],
                    ["'%s2'", 'alias_encoded']
                  ]
                })
              )}`

        break
      }
      case Provider.Snowflake: {
        const select = dedent`SELECT %s1
                        FROM (SELECT DISTINCT %s2 FROM ${connection.quoteName(preparedParams.source as string)})`
        const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(componentName, preparedParams.source)}`
          storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.source as string).split('.')
        }
        query = dedent`DECLARE
                  alias_encoded STRING;
                  alias_decoded STRING;
                BEGIN
                  ${
                    storedProc
                      ? dedent`
                          CREATE TABLE ${storedProcTable} AS
                          SELECT * FROM ${connection.quoteName(preparedParams.source as string)}
                          WHERE 1=0;`
                      : ''
                  }
                  LET res resultset := (EXECUTE IMMEDIATE '
                    with __alias AS(
                      SELECT REPLACE(
                        CASE
                          WHEN (data_type = ''GEOGRAPHY'' or data_type = ''GEOMETRY'') THEN ''ST_ASEWKT(%s) AS %s''
                          ELSE ''%s AS %s'' END
                        , ''%s'', column_name) col_alias_encoded,
                      REPLACE(
                        CASE data_type
                          WHEN ''GEOGRAPHY'' THEN ''TO_GEOGRAPHY(%s) AS %s''
                          WHEN ''GEOMETRY'' THEN ''TO_GEOMETRY(%s) AS %s''
                          ELSE ''%s AS %s'' END
                        , ''%s'', column_name) col_alias_decoded
                      FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                      WHERE table_name = ''${tablename.toUpperCase()}'' and
                        table_schema = ''${schemaname.toUpperCase()}'' and
                        table_catalog = ''${datasetname.toUpperCase()}''
                    )
                    SELECT LISTAGG(col_alias_encoded, '', ''), LISTAGG(col_alias_decoded, '', '')
                    FROM __alias
                  ');
                  LET cur_res CURSOR FOR res;
                  OPEN cur_res;
                  FETCH cur_res INTO alias_encoded, alias_decoded;
                  ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                  EXECUTE IMMEDIATE
                    REPLACE(
                      REPLACE(
                        'BEGIN
                          ${dedent(createTable)}
                        END;',
                        '%s1',
                        alias_decoded),
                    '%s2',
                    alias_encoded);
                END;`
        break
      }
      case Provider.Postgres: {
        const select = dedent`SELECT %s1
                        FROM (SELECT DISTINCT %s2 FROM ${connection.quoteName(preparedParams.source as string)}) t`
        const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(componentName, preparedParams.source)}`
          storedProcTable = connection.quoteName(`${connection.tempLocation}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.source as string).split('.')
        }
        query = dedent`DECLARE
                alias_encoded TEXT;
                alias_decoded TEXT;
                BEGIN
                  ${
                    storedProc
                      ? dedent`
                          CREATE TABLE ${storedProcTable} AS
                          SELECT * FROM ${connection.quoteName(preparedParams.source as string)}
                          WHERE 1=0;`
                      : ''
                  }
                  EXECUTE '
                    WITH __alias AS(
                      SELECT REPLACE(
                        CASE
                          WHEN (data_type = ''USER-DEFINED'' or data_type = ''GEOGRAPHY'' or data_type =''GEOMETRY'') THEN ''ST_ASEWKT(%s) AS %s''
                        ELSE ''%s AS %s'' END
                        , ''%s'', column_name) col_alias_encoded,
                      REPLACE(
                        CASE
                          WHEN (data_type = ''USER-DEFINED'' or data_type = ''GEOGRAPHY'') THEN ''ST_GEOGFROMTEXT(%s) AS %s''
                          WHEN (data_type = ''GEOGRAPHY'') THEN ''ST_GEOMFROMEWKT(%s) AS %s''
                        ELSE ''%s AS %s'' END
                        , ''%s'', column_name) col_alias_decoded
                      FROM "${datasetname}".INFORMATION_SCHEMA.COLUMNS
                      WHERE table_name ilike ''${tablename}'' and
                        table_schema ilike ''${schemaname}'' and
                        table_catalog ilike ''${datasetname}''
                    )
                    SELECT STRING_AGG(col_alias_encoded, '', '') alias_encoded, STRING_AGG(col_alias_decoded, '', '') alias_decoded
                    FROM __alias
                    ' INTO alias_encoded, alias_decoded;
                  ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                  EXECUTE
                    REPLACE(
                      REPLACE(
                        '${dedent(createTable)}',
                        '%s1',
                        alias_decoded),
                    '%s2',
                    alias_encoded);
                  END;`
        break
      }
      case Provider.Redshift: {
        const tempselect = connection.getTempSelectQuery(
          dedent`SELECT %s1
           FROM (
            SELECT DISTINCT %s2
            FROM
            ${connection.quoteName(preparedParams.source as string)}
           ) t`,
          dryRun
        )
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempLocation.split('.')
          tablename = `${tempTableName(componentName, preparedParams.source)}`
          storedProcTable = connection.quoteName(`${schemaname}.${tablename}`)
        } else {
          ;[datasetname, schemaname, tablename] = (preparedParams.source as string).split('.')
        }
        const temptable = `${outputTablename}_temp`
        query = dedent`
            DECLARE
              alias_encoded TEXT;
              alias_decoded TEXT;
            BEGIN
              ${
                storedProc
                  ? dedent`
                      CREATE TABLE ${storedProcTable} AS
                      SELECT * FROM ${connection.quoteName(preparedParams.source as string)}
                      WHERE 1=0;`
                  : ''
              }
              ${connection.getDropTableQuery(temptable)}
              EXECUTE '
                CREATE TABLE ${temptable} AS
                  SELECT REPLACE(
                    CASE
                      WHEN (data_type = ''geography'' or data_type =''geometry'') THEN ''ST_ASEWKT(%s) AS %s''
                    ELSE ''%s AS %s'' END
                    , ''%s'', column_name) col_alias_encoded,
                  REPLACE(
                    CASE
                      WHEN (data_type = ''geography'') THEN ''ST_GEOGFROMTEXT(%s) AS %s''
                      WHEN (data_type = ''geometry'') THEN ''ST_GEOMFROMEWKT(%s) AS %s''
                    ELSE ''%s AS %s'' END
                    , ''%s'', column_name) col_alias_decoded
                  FROM svv_columns
                  WHERE table_name ILIKE ''${tablename}'' AND
                    table_schema ILIKE ''${schemaname}'' AND
                    table_catalog ILIKE ''${datasetname}''
                ';
              EXECUTE '
                SELECT LISTAGG(col_alias_encoded, '', '') alias_encoded, LISTAGG(col_alias_decoded, '', '') alias_decoded
                FROM ${temptable};
              ' INTO alias_encoded, alias_decoded;
              ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
              ${connection.getDropTableQuery(outputTablename)}
              EXECUTE
                REPLACE(
                  REPLACE(
                    'CREATE TABLE ${outputTablename} AS
                    ${tempselect}',
                    '%s1',
                    alias_decoded),
                '%s2',
                alias_encoded);
              DROP TABLE ${temptable};
            END;`
        break
      }
      case Provider.DatabricksRest: {
        const select = `SELECT /*+ REPARTITION(40) */ DISTINCT * FROM ${connection.quoteName(
          preparedParams.source as string
        )}`
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return columns.source
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { RemoveDuplicatedRows }
